/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'

// Chakra Components
import {Box, Button, Flex, Image, Text, useStyleConfig} from '@chakra-ui/react'

// Project Components
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import InformativeLabel from '../informative-label'
import {remCalc} from '../../theme/_utils'
import InfoPopover from '../popovers/info-popover'
import {DeleteIcon, InfoIcon} from '../icons'
import parse from 'html-react-parser'

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const SubscriptionItem = ({details, handleRemoveItem, variant = 'subscriptions'}) => {
    const {currency: activeCurrency} = useCurrency()
    const style = useStyleConfig('ProductItem', {
        variant: variant,
    })
    const intl = useIntl()

    return (
        <Box {...style?.itemContainer}>
            <Flex {...style?.flexContainer}>
                <Image
                    role="img"
                    alt={intl.formatMessage({
                        id: 'item_image.alt.fallback',
                        defaultMessage: `Subscription image `,
                    })}
                    {...style?.cartImage}
                    src={details?.image}
                    ignoreFallback={true}
                />

                <Box {...style?.detailContainer}>
                    {variant === 'subscriptions' && (
                        <Flex {...style?.infoLabelContainer}>
                            <InformativeLabel
                                label={intl.formatMessage({
                                    defaultMessage: 'Subscription',
                                    id: 'informative_label.subscription',
                                })}
                                variant="light"
                                data-testid="product-officina-badge"
                            />
                        </Flex>
                    )}

                    <Box {...style?.headerStack} gap={remCalc(8)}>
                        <Text {...style?.itemName}>{details?.subscriptionName}</Text>
                        {handleRemoveItem && (
                            <Box {...style?.removeIcon}>
                                <Button
                                    onClick={handleRemoveItem}
                                    data-testid="cart-item-remove-button"
                                    className="js-personalization-remove-button"
                                >
                                    <DeleteIcon w="16px" h="16px" />
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <Box {...style?.omniAndPrice}>
                        <>
                            {details?.recurringOrderTotalPrice > details?.firstOrderTotalPrice ? (
                                <Box {...style?.omnibus}>
                                    <Text {...style?.recurringPrice}>
                                        <FormattedNumber
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                            value={details?.recurringOrderTotalPrice}
                                        />
                                    </Text>
                                    <Text>
                                        <FormattedNumber
                                            style="currency"
                                            currency={activeCurrency}
                                            value={details?.firstOrderTotalPrice}
                                        />
                                    </Text>
                                </Box>
                            ) : (
                                <Text>
                                    <FormattedNumber
                                        style="currency"
                                        currency={activeCurrency}
                                        value={details?.recurringOrderTotalPrice}
                                    />
                                </Text>
                            )}
                            {details?.subFrequency === '1' ? (
                                <Text>
                                    /{' '}
                                    <FormattedMessage
                                        id="subscriptions.labels.month"
                                        defaultMessage="month"
                                    />
                                </Text>
                            ) : (
                                <Text>
                                    / {details?.subFrequency}{' '}
                                    <FormattedMessage
                                        id="subscriptions.labels.months"
                                        defaultMessage="months"
                                    />
                                </Text>
                            )}
                        </>
                    </Box>

                    {variant !== 'subscriptionsOrderSummary' &&
                        variant !== 'subscriptionsConfirmation' &&
                        parseFloat(details?.discountPercentageFirstOrder || '0') > 0 && (
                            <Text {...style?.discountDescriptionContainer}>
                                <FormattedMessage
                                    id="subscriptions.labels.discount_description"
                                    defaultMessage="{discountPercentageFirstOrder}% discount on your first order, then {recurringOrderTotalPrice}€ / month"
                                    values={{
                                        discountPercentageFirstOrder:
                                            details?.discountPercentageFirstOrder,
                                        recurringOrderTotalPrice: details?.recurringOrderTotalPrice,
                                    }}
                                />
                            </Text>
                        )}
                    <Box
                        {...style?.longDescriptionContainer}
                        __css={{
                            '& li:not(:last-child)': {
                                marginBottom: remCalc(6),
                            },
                        }}
                    >
                        {parse(details?.longDescription || '')}
                    </Box>
                    {variant === 'subscriptions' && (
                        <Box {...style?.popoverContainer}>
                            <Text>
                                <FormattedMessage
                                    id="subscriptions.labels.details"
                                    defaultMessage="Detail subscription"
                                />
                            </Text>
                            <InfoPopover
                                description={
                                    <FormattedMessage
                                        id="subscriptions.labels.popoverDescription"
                                        defaultMessage="Cost per Coffee: €{costPerCoffee} - No. of Coffees/Day: {coffeePerDay} - Free Shipping - Duration of {subDuration} months - Cancel anytime with no additional costs"
                                        values={{
                                            costPerCoffee: details?.costPerCoffee,
                                            coffeePerDay: details?.coffeePerDay,
                                            subDuration: details?.subDuration,
                                        }}
                                    />
                                }
                                showInfoIcon={false}
                                openTooltipOn="hover"
                                trigger={
                                    <Box display="flex" alignItems="center" gap="2">
                                        <InfoIcon
                                            boxSize={'iconsSize.small'}
                                            fill="button.text.secondary-default"
                                            color="text.default"
                                            cursor="pointer"
                                        />
                                    </Box>
                                }
                                variant="right-bottom"
                            />
                        </Box>
                    )}
                </Box>
            </Flex>
        </Box>
    )
}

SubscriptionItem.propTypes = {
    details: PropTypes.object,
    handleRemoveItem: PropTypes.func,
    variant: PropTypes.string,
}

export default SubscriptionItem
